import { type ReactNode, useState } from "react";

import Alerts from "common/components/subscriptions/AgencySubscription/Alerts";
import PricingDetails from "common/components/subscriptions/AgencySubscription/PricingDetails";
import useCustomerEstimateCreation from "common/datahooks/useCustomerEstimateCreation";
import useCustomerSubscriptionMutations from "common/datahooks/useCustomerSubscriptionMutations";
import usePromotions from "common/datahooks/usePromotions";
import { trackMetaPixel } from "common/helpers/utils";
import type { Pricing } from "common/types";

import QuantitySelector from "../QuantitySelector";

const INCLUDED_SEATS = import.meta.env.MODE === "production" ? 20 : 2;
const MAX_SEATS = 999;

export default function AgencySubscription({
  children,
  currentStep,
  onSuccess,
  pricing,
}: {
  children: (params: {
    isNextStepDisabled: boolean;
    isNextButtonLoading: boolean;
    onCreateSubscription: (paymentSourceId: string) => void;
    mainContent: ReactNode;
  }) => ReactNode;
  currentStep: "manage" | "summary" | "payment" | "cancelAccounts" | "success";
  onSuccess: () => void;
  pricing: { addon: Pricing; plan: Pricing };
}) {
  const { agencyPlan } = usePromotions();

  const [seatCount, setSeatCount] = useState<number>(
    agencyPlan ? agencyPlan.license_count : INCLUDED_SEATS,
  );

  const { estimateCreation } = useCustomerEstimateCreation({
    planPricingId: pricing.plan.id,
    quantity: seatCount,
    subscription: agencyPlan?.subscription,
  });
  const {
    createSubscription,
    isCreatingSubscription,
    updateSubscription,
    isUpdatingSubscription,
  } = useCustomerSubscriptionMutations();

  const additionalSeatsCount = seatCount - INCLUDED_SEATS;
  const planPrice = estimateCreation
    ? additionalSeatsCount * (estimateCreation.pricePerItem / 100)
    : 0;
  const assignedLicenseCount = agencyPlan
    ? agencyPlan.license_count - agencyPlan.unassigned_license_count
    : 0;

  async function onCreateSubscription(paymentSourceId: string) {
    const subscriptionData = {
      planPricingId: pricing.plan.id,
      count: seatCount,
      paymentSourceId,
    };
    if (agencyPlan?.subscription.status === "active") {
      await updateSubscription(subscriptionData);
    } else {
      const {
        subscription: { id },
      } = await createSubscription(subscriptionData);
      const eventData = {
        value: planPrice,
        currency: "USD",
        content_type: "subscription",
        content_ids: [id],
        num_items: seatCount,
      };

      trackMetaPixel("track", "Subscribe", eventData);
      trackMetaPixel("track", "Purchase", eventData);
    }

    onSuccess();
  }

  //all the limits below should not apply for subscriptions that are cancelled
  const isDowngrade =
    agencyPlan?.subscription.status === "active" &&
    seatCount < agencyPlan.license_count;

  const isUnchangedSubscription =
    agencyPlan?.subscription.status === "active"
      ? agencyPlan.license_count === seatCount &&
        agencyPlan.subscription.item_price.id === pricing.plan.id
      : false;

  const pricingDetailsComponent = (
    <PricingDetails
      pricing={pricing}
      seatCount={seatCount}
      includedSeats={INCLUDED_SEATS}
      isDowngrade={isDowngrade}
      alertsComponent={
        currentStep === "manage" && (
          <Alerts seatCount={seatCount} includedSeats={INCLUDED_SEATS} />
        )
      }
    />
  );

  const mainContent =
    currentStep === "manage" ? (
      <>
        <QuantitySelector
          quantity={seatCount}
          setQuantity={setSeatCount}
          minValue={INCLUDED_SEATS}
          maxValue={MAX_SEATS}
        />
        <div className="flex flex-col gap-y-2.5">{pricingDetailsComponent}</div>
      </>
    ) : (
      pricingDetailsComponent
    );

  return children({
    isNextStepDisabled:
      isUnchangedSubscription || seatCount < assignedLicenseCount,
    isNextButtonLoading: isCreatingSubscription || isUpdatingSubscription,
    onCreateSubscription,
    mainContent,
  });
}
